/*
 * DocApp.tsx
 * based on DevPageApp Created in March 2020 by Maximillian Dornseif
 *
 * Created by Dr. Maximillian Dornseif 2021-11-23 in huWaWi3 24.4.0
 * Copyright (c) 2021, 2023 HUDORA GmbH
 */

import 'graphiql/graphiql.min.css'

import { useQuery } from '@apollo/client'
import { LadeChecker, ShowSchema } from '@hudora/hd-react-components'
import belegeMarkdownPath from '@hudora/huwawi3-main/docs/Belege.md'
import fakturaMarkdownPath from '@hudora/huwawi3-main/docs/Faktura.md'
import inventurMarkdownPath from '@hudora/huwawi3-main/docs/Inventur.md'
import serverMarkdownPath from '@hudora/huwawi3-main/docs/Server.md'
import shopwareMarkdownPath from '@hudora/huwawi3-main/docs/Shopware.md'
import { ISidebarProps, WithSidebar } from 'fluentui-hooks'
import gql from 'graphql-tag'
import Markdown from 'markdown-to-jsx'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider, useQuery as qyUseQuery } from 'react-query'

import frontendChangelogPath from '../CHANGELOG.md'
import { HdSpinner } from '../lib/components/widgets/HdSpinner'

const queryClient = new QueryClient()

export function DocApp(props) {
  const [frontendChangelog, setFrontendChangelog] = useState('')
  const [belegeMarkdown, setBelegeMarkdown] = useState('')
  const [fakturaMarkdown, setFakturaMarkdown] = useState('')
  const [shopwareMarkdown, setShopwareMarkdown] = useState('')
  const [serverMarkdown, setServerMarkdown] = useState('')
  const [inventurMarkdown, setInventurMarkdown] = useState('')

  // Frontend-Changelog lokal lesen
  useEffect(() => {
    fetch(frontendChangelogPath, { headers: { 'x-msw-bypass': 'true' } })
      .then(async (response) => setFrontendChangelog(await response.text()))
      .catch(
        // in den Unittests wirft das immer einen Fehler
        (e) => {
          console.error('Changelog loading error ', e)
        }
      )
  }, [setFrontendChangelog])
  useEffect(() => {
    fetch(belegeMarkdownPath, { headers: { 'x-msw-bypass': 'true' } })
      .then(async (response) => setBelegeMarkdown(await response.text()))
      .catch(
        // in den Unittests wirft das immer einen Fehler
        (e) => {
          console.error('Doc loading error ', e)
        }
      )
  }, [setShopwareMarkdown])
  useEffect(() => {
    fetch(fakturaMarkdownPath, { headers: { 'x-msw-bypass': 'true' } })
      .then(async (response) => setFakturaMarkdown(await response.text()))
      .catch(
        // in den Unittests wirft das immer einen Fehler
        (e) => {
          console.error('Doc loading error ', e)
        }
      )
  }, [setFakturaMarkdown])
  useEffect(() => {
    fetch(serverMarkdownPath, { headers: { 'x-msw-bypass': 'true' } })
      .then(async (response) => setServerMarkdown(await response.text()))
      .catch(
        // in den Unittests wirft das immer einen Fehler
        (e) => {
          console.error('Doc loading error ', e)
        }
      )
  }, [setServerMarkdown])
  useEffect(() => {
    fetch(shopwareMarkdownPath, { headers: { 'x-msw-bypass': 'true' } })
      .then(async (response) => setShopwareMarkdown(await response.text()))
      .catch(
        // in den Unittests wirft das immer einen Fehler
        (e) => {
          console.error('Doc loading error ', e)
        }
      )
  }, [setShopwareMarkdown])
  useEffect(() => {
    fetch(inventurMarkdownPath, { headers: { 'x-msw-bypass': 'true' } })
      .then(async (response) => setInventurMarkdown(await response.text()))
      .catch(
        // in den Unittests wirft das immer einen Fehler
        (e) => {
          console.error('Doc loading error ', e)
        }
      )
  }, [setInventurMarkdown])

  const schemaDoc = {
    BBeleg: 'Generischer Beleg',
    BLieferschein: '- Lieferschein',
    BRechnung: '- Rechnung',

    H3AuftragsEntwurf: 'Auftragsentwurf',
    BAuftrag: 'Auftragsstatus',

    HwKunde: 'Kunde',
    H3KundeInfo: '- CRM',
    HwKundeEdiConfig: '- EDI Einstellungen',
    HwLieferadresse: '- Lieferadresse',
    HwVersandanweisung: '- Versandanweisung',
    HwKundenPreis: '- Kunden Preis',
    HwKundenGruppe: 'Gruppe',
    HwKundenVerband: 'Verband',

    H3Produkt: 'Produkt',
    H3ProduktInfo: '- PIM',
    H3Item: 'Item',
    H3ItemInfo: '- IIM/PIM',
    HwArtikelKonto: '- Konto',
    HwArtikelZukunft: '- Zukunft',
    HwPreis: '- Preis',
    H3SkuMaster: 'SKUMaster',
    H3SkuMasterInfo: '- SMIM//PIM',
    H3Sku: 'SKU',
    H3SkuInfo: '- SIM/PIM',
    HwIcZuteilung: '- Zuteilung',
    HwIcBestandsbuchEintrag: '- Bestandsbuch',
    HwIcWertkorrektur: '- Wertkorrektur',

    HwLieferant: 'Lieferant',
    HwEinkaufspreis: '- Einkaufspreis',

    HwAuftrag: 'Auftrag',
    HwAuftragsPosition: '- A. Position',
    HwAuditLog: '- A. Audit',

    HwKommiAuftrag2: 'Kommiauftrag',
    HwKommiAuftragsPosition2: '- K. Positon',

    HwBestellung: 'Bestellung',
    HwBestellPosition: '- B. Position',
    HwBestellPositionLieferplan: '- B. Lieferplan',
    // FobOrderHead.yaml
    // FobOrderPosition.yaml
    // FobOrderDocument.yaml

    AaaUser: 'Nutzer',
    AaaRole: 'Nutzer-Rolle',
    AaaPermission: 'Berechtigung',
    H3DbAudit: 'DB-Changelog',

    EBEinkaufsBeleg: 'Generischer Einkaufs-Beleg',
    EBInvoice: '- huFob Rechnung',
    EBOfferSheet: '- huFob Angebot',
    EBPuchaseOrder: '- huFob und huWaWi PO',
  }

  const SIDEBAR: ISidebarProps = {
    Changelog: {
      links: [
        {
          title: 'Frontend',
          path: '/doc/c/frontend',
          component: <ShowChangelog data={frontendChangelog} />,
        },
        {
          title: 'Backend',
          path: '/doc/c/backend',
          component: <BackendChangelog />,
        },
        {
          title: 'Batch',
          path: '/doc/c/batch',
          component: <BatchPyChangelog />,
        },
        {
          title: 'huWaWi2',
          path: '/doc/c/huwawi2',
          component: <HuWaWi2Changelog />,
        },
      ],
    },
    Dokumentation: {
      links: [
        {
          title: 'Belege',
          path: '/doc/d/belege',
          component: <Markdown>{belegeMarkdown}</Markdown>,
        },
        {
          title: 'Faktura',
          path: '/doc/d/faktura',
          component: <Markdown>{fakturaMarkdown}</Markdown>,
        },
        {
          title: 'Shopware',
          path: '/doc/d/shopware',
          component: <Markdown>{shopwareMarkdown}</Markdown>,
        },
        {
          title: 'Inventur',
          path: '/doc/d/inventur',
          component: <Markdown>{inventurMarkdown}</Markdown>,
        },
        {
          title: 'Server',
          path: '/doc/d/server',
          component: <Markdown>{serverMarkdown}</Markdown>,
        },
      ],
    },
    Schemata: {
      links: Object.entries(schemaDoc).map(([k, v]) => ({
        title: v,
        path: `/doc/s/${k}`,
        component: <ShowSchema schemaName={k} />,
      })),
    },
  }

  return (
    <QueryClientProvider client={queryClient}>
      <WithSidebar items={SIDEBAR}>
        <ShowChangelog data={frontendChangelog} />
      </WithSidebar>
    </QueryClientProvider>
  )
}

/** Das Changelog vom Backend per GraphQL abrufen
 * */
function BackendChangelog() {
  const { loading, error, data } = useQuery(CHANGELOG_QUERY)

  return (
    <>
      <LadeChecker loading={loading} error={error} label="Lade Changelog" />
      <ShowChangelog data={data?.server?.changelog} />
    </>
  )
}

const CHANGELOG_QUERY = gql`
  query ChangelogQuery {
    server {
      changelog
    }
  }
`

/** Das Changelog von BatchPy per REST abrufen
 * */
function BatchPyChangelog() {
  const { data, error, isLoading } = qyUseQuery(
    'batchData',
    () => fetch('/batch/CHANGELOG.md').then((res) => res.text()),
    {
      retry: true,
    }
  )
  return (
    <>
      <LadeChecker loading={isLoading} error={error} label="Lade Changelog" />
      <ShowChangelog data={data} />
    </>
  )
}
/** Das Changelog von huWaWi2 per REST abrufen
 * */
function HuWaWi2Changelog() {
  const { data, error, isLoading } = qyUseQuery(
    'huWaWi2Data',
    () => fetch('http://huwawi.hudora.de/CHANGELOG.md').then((res) => res.text()),
    {
      retry: true,
    }
  )
  return (
    <>
      <LadeChecker loading={isLoading} error={error} label="Lade Changelog" />
      <ShowChangelog data={data} />
    </>
  )
}

function ShowChangelog(props) {
  if (!props.data) {
    return <HdSpinner />
  }
  if (props.data === '') {
    return <HdSpinner />
  }
  return <Markdown>{props.data}</Markdown>
}
ShowChangelog.propTypes = {
  data: PropTypes.string,
}
